import type { Basis } from '@afra/shared'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../../lib/api'

const BASIS_FA: Record<Basis, string> = {
  self_report: 'اظهار مؤسسه',
  explained: 'همراه با توضیح',
  document_verified: 'مستند',
  assessor_verified: 'تأیید ارزیاب',
  legacy_quiz: 'خودارزیابی پیشین',
  inferred: 'استنتاجی',
}

export function LedgerPage() {
  const [open, setOpen] = useState<string | null>(null)
  const { data, isLoading } = useQuery({ queryKey: ['ledger'], queryFn: api.ledger })

  if (isLoading || !data) {
    return (
      <div className="container center">
        <p className="muted">در حال بارگذاری…</p>
      </div>
    )
  }

  return (
    <div className="container stack">
      <div className="card">
        <h1>بلوغ سازمانی</h1>
        <p className="meta">
          آنچه تا امروز درباره مؤسسه شما می‌دانیم: <span className="num">{data.known}</span> گزاره از{' '}
          <span className="num">{data.total}</span> گزاره استاندارد افرا. کنار هر عدد نوشته‌ام که بر چه
          پایه‌ای به دست آمده، چون «گفته‌ایم» با «نشان داده‌ایم» یکی نیست.
        </p>
      </div>

      <div className="card">
        {data.dimensions.map((d) => {
          const percent = d.total === 0 ? 0 : (d.known / d.total) * 100
          const bases = Object.entries(d.byBasis).filter(([, n]) => n > 0)
          return (
            <div key={d.code} className="dimension">
              <div>
                <div className="dimension__title">{d.titleFa}</div>
                <div className="meta">
                  <span className="num">{d.known}</span> از <span className="num">{d.total}</span> گزاره
                  {bases.length > 0 && (
                    <> · {bases.map(([b, n]) => `${BASIS_FA[b as Basis] ?? b}: ${n}`).join(' · ')}</>
                  )}
                </div>
              </div>
              <div className="dimension__score">
                {d.meanScore === null ? '—' : d.meanScore.toFixed(1)}
              </div>
              <div className="dimension__bar">
                <div className="rail-bar">
                  <div className="rail__fill" style={{ width: `${percent}%` }} />
                </div>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <button
                  type="button"
                  className="btn btn--quiet btn--sm"
                  onClick={() => setOpen(open === d.code ? null : d.code)}
                >
                  {open === d.code ? 'بستن' : 'دیدن گزاره‌ها'}
                </button>
              </div>
              {open === d.code && <DimensionDetail code={d.code} />}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function DimensionDetail({ code }: { code: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['dimension', code],
    queryFn: () => api.dimension(code),
  })

  if (isLoading || !data) {
    return (
      <p className="meta" style={{ gridColumn: '1 / -1' }}>
        …
      </p>
    )
  }

  return (
    <div style={{ gridColumn: '1 / -1', marginTop: 'var(--space-3)' }}>
      {data.statements.map((s) => (
        <div
          key={`${s.statementCode}-${s.levelNo}`}
          style={{ padding: 'var(--space-3) 0', borderTop: '1px solid var(--line)' }}
        >
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span className="meta">
              گزاره {s.statementCode} · سطح <span className="num">{s.levelNo}</span>
            </span>
            {s.score === null ? (
              <span className="badge">هنوز نپرسیده‌ایم</span>
            ) : (
              <span className={`badge ${s.score >= 2 ? 'badge--good' : 'badge--warn'}`}>
                <span className="num">{s.score}</span> از <span className="num">۳</span> ·{' '}
                {BASIS_FA[s.basis] ?? s.basis}
              </span>
            )}
          </div>
          <div style={{ lineHeight: 1.9 }}>{s.textFa}</div>
          {s.rationaleFa && <div className="meta">{s.rationaleFa}</div>}
        </div>
      ))}
    </div>
  )
}
